import { G } from '@mobily/ts-belt';
import { create } from 'axios';
import { magicLinkClient, twoFactorClient } from 'better-auth/client/plugins';
import { createAuthClient, StandardSchemaV1 } from 'better-auth/react';

import { AssociatedAccounts } from '@Common/types/auth/associated_accounts';
import type { OtpChannel } from '@Common/types/auth/otp_channel';

import { getCurrentScreen } from '@sh4res/bo-common/utils/current_screen';
import { getDeviceId } from '@sh4res/bo-common/utils/device_id';
import { getEnvVar } from '@sh4res/bo-common/utils/env';

import type { createAuthEndpoint } from 'better-auth/api';

const SESSION_MUTATING_PATHS = [
  '/sign-in',
  '/sign-out',
  '/two-factor/verify',
  '/phone-number/verify',
  '/users/active',
];

export const authClient = createAuthClient({
  baseURL: getEnvVar('REACT_APP_API_ORIGIN') || window.origin,
  fetchOptions: {
    headers: {
      deviceid: getDeviceId(),
    },
    // `headers` above is evaluated once, at client creation — the current screen has to be read
    // per request or every auth call would report the page the app booted on
    onRequest: (context) => {
      context.headers.set('bo-current-screen', getCurrentScreen());
    },
    onResponse: (context) => {
      const { response } = context;

      if (response.ok) {
        const { url } = response;

        if (SESSION_MUTATING_PATHS.some((path) => url.includes(path))) {
          invalidateSessionCache();
        }
      }
    },
  },
  plugins: [
    magicLinkClient(),
    twoFactorClient(),
    {
      id: 'roles',
      $InferServerPlugin: {} as {
        id: 'roles';
        schema: {
          user: {
            fields: {
              role: {
                type: ['b2b_admin', 'b2b_distributor', 'b2b_inspector'];
              };
            };
          };
        };
      },
    },
    {
      id: 'impersonation',
      $InferServerPlugin: {} as {
        id: 'impersonation';
        schema: {
          session: {
            fields: {
              impersonatedBy: {
                type: 'string';
                required: false;
              };
            };
          };
        };
      },
    },
    {
      id: 'phone-number',
      $InferServerPlugin: {} as {
        id: 'phone-number';
        endpoints: {
          sendPhoneNumberOTP: ReturnType<
            typeof createAuthEndpoint<
              '/phone-number/send-otp',
              {
                method: 'POST';
                body: StandardSchemaV1<
                  { phoneNumber: string | null; companyCode?: number | null },
                  unknown
                >;
              },
              { status: boolean }
            >
          >;
          verifyPhoneNumber: ReturnType<
            typeof createAuthEndpoint<
              '/phone-number/verify',
              {
                method: 'POST';
                body: StandardSchemaV1<{ code: string }, unknown>;
              },
              { status: boolean }
            >
          >;
        };
        schema: {
          user: {
            fields: {
              phoneNumber: {
                type: 'string';
                required: false;
              };
              phoneNumberVerified: {
                type: 'boolean';
              };
            };
          };
        };
      },
    },
    {
      id: 'alternate-email',
      $InferServerPlugin: {} as {
        id: 'alternate-email';
        schema: {
          user: {
            fields: {
              alternateEmail: {
                type: 'string';
                required: false;
              };
            };
          };
        };
      },
    },
    {
      id: 'mfa-password-reset',
      $InferServerPlugin: {} as {
        id: 'mfa-password-reset';
        endpoints: {
          sendPasswordResetOTP: ReturnType<
            typeof createAuthEndpoint<
              '/password-reset/send-otp',
              {
                method: 'POST';
                body: StandardSchemaV1<{ token: string }, unknown>;
              },
              { status: boolean }
            >
          >;
          verifyPasswordResetOTP: ReturnType<
            typeof createAuthEndpoint<
              '/password-reset/verify',
              {
                method: 'POST';
                body: StandardSchemaV1<{ token: string; code: string }, unknown>;
              },
              { status: boolean }
            >
          >;
        };
      },
    },
    {
      id: 'otp-channel',
      $InferServerPlugin: {} as {
        id: 'otp-channel';
        endpoints: {
          getOtpChannel: ReturnType<
            typeof createAuthEndpoint<'/two-factor/otp-channel', { method: 'GET' }, OtpChannel>
          >;
        };
      },
    },
    {
      id: 'qa',
      $InferServerPlugin: {} as {
        id: 'qa';
        endpoints: {
          getOTPCode: ReturnType<
            typeof createAuthEndpoint<
              '/qa/otp-code',
              { method: 'GET'; query: StandardSchemaV1<{ token?: string }, unknown> },
              { code: string }
            >
          >;
          enable2FA: ReturnType<
            typeof createAuthEndpoint<'/qa/enable-2fa', { method: 'POST' }, { status: boolean }>
          >;
        };
      },
      pathMethods: {
        '/qa/enable-2fa': 'POST',
      },
    },
    {
      id: 'account-selector',
      $InferServerPlugin: {} as {
        id: 'account-selector';
        schema: {
          session: {
            fields: {
              selectedUserId: {
                type: 'number';
                required: false;
              };
            };
          };
        };
        endpoints: {
          getAssociatedAccounts: ReturnType<
            typeof createAuthEndpoint<
              '/users/list',
              { method: 'GET' },
              {
                users: AssociatedAccounts;
              }
            >
          >;
          setActiveAccount: ReturnType<
            typeof createAuthEndpoint<
              '/users/active',
              { method: 'POST'; body: StandardSchemaV1<{ userId: number }, unknown> },
              { status: boolean }
            >
          >;
        };
      },
    },
  ],
  disableDefaultFetchPlugins: true,
});

export const publicAuthClient = create({
  baseURL: `${getEnvVar('REACT_APP_API_ORIGIN') || window.origin}/any/apibo_external`,
});

publicAuthClient.interceptors.request.use((config) => {
  config.headers.set('bo-current-screen', getCurrentScreen());

  return config;
});

type GetSessionResult = {
  data: typeof authClient.$Infer.Session | null;
  error: {
    status: number;
    statusText: string;
    code?: string;
    message?: string;
  } | null;
};

let cachedSessionPromise: Promise<GetSessionResult> | null = null;
let cacheExpiry = 0;

const SESSION_CACHE_TTL_MS = 5000;

export async function getAuthSession(forceFetch = false): Promise<GetSessionResult> {
  const now = Date.now();

  if (forceFetch || G.isNullable(cachedSessionPromise) || now > cacheExpiry) {
    const promise = (async () => {
      try {
        return await authClient.getSession();
      } catch (error) {
        invalidateSessionCache();
        throw error;
      }
    })();

    cachedSessionPromise = promise;

    cacheExpiry = now + SESSION_CACHE_TTL_MS;
  }

  return cachedSessionPromise;
}

function invalidateSessionCache() {
  cachedSessionPromise = null;
  cacheExpiry = 0;
}
